import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireAuth } from '@techsofit/app-kit';
import { BrandSplash } from '@techsofit/ui';
import { AppShell } from './components/AppShell';
import { AccountAuthPage } from './pages/AccountAuth';
import { PricingPage } from './pages/Pricing';
import { DashboardPage } from './pages/Dashboard';
import { MembersPage } from './pages/Members';
import { MemberHealthPage } from './pages/MemberHealth';
import { LeadsPage } from './pages/Leads';
import { CorporatePage } from './pages/Corporate';
import { MemberDetailPage } from './pages/MemberDetail';
import { MemberFormPage } from './pages/MemberForm';
import { GoalFormPage } from './pages/GoalForm';
import { GoalDetailPage } from './pages/GoalDetail';
import { CheckInsPage } from './pages/CheckIns';
import { AttendancePage } from './pages/Attendance';
import { CheckInPage } from './pages/CheckIn';
import { WrappedPage } from './pages/Wrapped';
import { WrappedPublicPage } from './pages/WrappedPublic';
import { RecordPaymentPage } from './pages/RecordPayment';
import { PaymentsPage } from './pages/Payments';
import { FinancePage } from './pages/Finance';
import { PlansPage } from './pages/Plans';
import { BillingPage } from './pages/Billing';
import { StaffPage } from './pages/Staff';
import { GuestsPage } from './pages/Guests';
import { SettingsPage } from './pages/Settings';
import { NotificationsPage } from './pages/Notifications';
import { ReportsPage } from './pages/Reports';
import { ExerciseLibraryPage } from './pages/ExerciseLibrary';
import { WorkoutsLayout } from './pages/WorkoutsLayout';
import { WorkoutsListPage } from './pages/WorkoutsList';
import { WorkoutBuilderPage } from './pages/WorkoutBuilder';
import { WorkoutGroupsPage } from './pages/WorkoutGroups';
import { WorkoutSchedulePage } from './pages/WorkoutSchedule';
import { CompliancePage } from './pages/Compliance';
import { MemberPortalPage } from './pages/MemberPortal';
import { SupportEnterPage } from './pages/SupportEnter';

/** Gym management system: owner/staff app, member portal, and gym signup. */
export const router = createBrowserRouter([
  // Gym-only passwordless door: /account logs in, /signup registers a gym.
  { path: '/account', element: <AccountAuthPage /> },
  { path: '/start', element: <Navigate to="/account" replace /> },
  { path: '/login', element: <Navigate to="/account" replace /> },
  { path: '/signup', element: <AccountAuthPage initialMode="signup" /> },
  { path: '/pricing', element: <PricingPage /> },
  { path: '/w/:token', element: <WrappedPublicPage /> },
  { path: '/me', element: <MemberPortalPage /> },
  { path: '/support/enter', element: <SupportEnterPage /> },
  { path: '/checkin', element: <CheckInPage /> },
  {
    path: '/',
    element: (
      <RequireAuth fallback={<BrandSplash />}>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'members', element: <MembersPage /> },
      { path: 'health', element: <MemberHealthPage /> },
      { path: 'leads', element: <LeadsPage /> },
      { path: 'corporate', element: <CorporatePage /> },
      { path: 'members/new', element: <MemberFormPage /> },
      { path: 'members/:id', element: <MemberDetailPage /> },
      { path: 'members/:id/edit', element: <MemberFormPage /> },
      { path: 'members/:id/goals/new', element: <GoalFormPage /> },
      { path: 'goals/:goalId', element: <GoalDetailPage /> },
      { path: 'members/:id/wrapped', element: <WrappedPage /> },
      { path: 'attendance', element: <AttendancePage /> },
      { path: 'check-ins', element: <Navigate to="/attendance" replace /> },
      { path: 'goal-check-ins', element: <CheckInsPage /> },
      {
        path: 'workouts',
        element: <WorkoutsLayout />,
        children: [
          { index: true, element: <WorkoutsListPage /> },
          { path: 'library', element: <ExerciseLibraryPage /> },
          { path: 'groups', element: <WorkoutGroupsPage /> },
          { path: 'schedule', element: <WorkoutSchedulePage /> },
          { path: 'compliance', element: <CompliancePage /> },
        ],
      },
      { path: 'workouts/new', element: <WorkoutBuilderPage /> },
      { path: 'workouts/:id/edit', element: <WorkoutBuilderPage /> },
      { path: 'payments/new', element: <RecordPaymentPage /> },
      { path: 'payments', element: <PaymentsPage /> },
      { path: 'finance', element: <FinancePage /> },
      { path: 'plans', element: <PlansPage /> },
      { path: 'billing', element: <BillingPage /> },
      { path: 'staff', element: <StaffPage /> },
      { path: 'guests', element: <GuestsPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'settings', element: <SettingsPage /> },
    ],
  },
  { path: '*', element: <Navigate to="/dashboard" replace /> },
]);
